import type { Ref } from 'vue';
import { computed, nextTick, ref, unref } from 'vue';
import { ActiveReturnMode } from '@/types/ActiveReturnMode';
import { useRoute } from 'vue-router';
import useRouteChecks from './useRouteChecks';
import type { MaybeRef } from '@vueuse/core';
import { createGlobalState } from '@vueuse/core';
import useTranslation from './useTranslation';
import { useGetBlmViews } from './queries/useBlmView';
import { useGetRegimeSets } from './queries/useRegimeAnalysis';
import type { BLMView } from '@/api-v2/web/blm';
import type { FactsheetPdfParams } from '@/types/FactsheetPdfParams';
import { isFactsheetActiveReturnPdfParams } from '@/types/FactsheetPdfParams';
import type { LoadingWhenEnabledQRT, VQQueryOptions } from '@/types/VueQueryTypes';
import useDefaultActiveReturnRebalancingType from './useDefaultActiveReturnRebalancingType';
import type { ActiveReturnRebalancingType } from '@/types/ActiveReturnRebalancingType';
import type { RegimeAnalysisAttributeResponseDTO } from '@/api-v2/web/regime-analysis';
import type { UseQueryReturnType } from '@tanstack/vue-query';

export const useActiveReturnState = createGlobalState(
  (): { activeReturnMode: Ref<ActiveReturnMode>; activeReturnRebalancingType: Ref<ActiveReturnRebalancingType> } => {
    const activeReturnMode = ref<ActiveReturnMode>(ActiveReturnMode.TOTAL_RETURN);

    const { DEFAULT_ACTIVE_RETURN_REBALANCING_TYPE } = useDefaultActiveReturnRebalancingType();

    const activeReturnRebalancingType = ref(DEFAULT_ACTIVE_RETURN_REBALANCING_TYPE);

    return {
      activeReturnMode,
      activeReturnRebalancingType,
    };
  },
);

function useActiveReturnMode(): {
  activeReturnMode: Ref<ActiveReturnMode>;
  activeReturnRebalancingType: Ref<ActiveReturnRebalancingType>;
  activeReturnModeOptions: Ref<{ value: ActiveReturnMode; text: string }[]>;
  activeReturnModeText: Ref<string>;
  getActiveReturnModeText: (mode?: MaybeRef<ActiveReturnMode>) => string;
  isActiveReturnStateActive: Ref<boolean>;
} {
  const { translate } = useTranslation();

  const { activeReturnMode, activeReturnRebalancingType } = useActiveReturnState();
  const route = useRoute();
  const { isOnFactorDecomposition, isOnCombinedModulePDF } = useRouteChecks(route);
  const pdfParams = computed(() => route.query as FactsheetPdfParams);
  const isActiveReturnInPdfParams = computed(() => isFactsheetActiveReturnPdfParams(pdfParams.value));

  /**
   * In combined module pdf, we use pdfParams to override isActiveReturnStateActive instead of checking activeReturnMode.value
   * This is to keep combined module pdf factor decomposition section always be showing in excess return
   */
  const isActiveReturnStateActive = computed(() => {
    if (isOnCombinedModulePDF.value && isActiveReturnInPdfParams.value) {
      return true;
    }

    return activeReturnMode.value === ActiveReturnMode.ACTIVE_RETURN;
  });

  const activeReturnModeOptions = computed(() => {
    const returnOption = {
      value: ActiveReturnMode.TOTAL_RETURN,
      text: translate({
        path: 'GLOBAL.RETURN_TYPE_CONSTANTS',
        item: 'RETURN',
      }),
    };
    const activeReturnOption = {
      value: ActiveReturnMode.ACTIVE_RETURN,
      text: translate({ path: 'GLOBAL.RETURN_TYPE_CONSTANTS', item: ActiveReturnMode.ACTIVE_RETURN }),
    };
    /**
     * Currently this is only implemented for Factor Decomposition, so we hide it if we're not on FD
     */
    const excessReturnOption = {
      value: ActiveReturnMode.EXCESS_RETURN,
      text: translate({ path: 'GLOBAL.RETURN_TYPE_CONSTANTS', item: ActiveReturnMode.EXCESS_RETURN }),
    };

    if (isOnFactorDecomposition.value) {
      return [returnOption, activeReturnOption, excessReturnOption];
    }

    return [returnOption, activeReturnOption];
  });

  const getActiveReturnModeText = (mode?: MaybeRef<ActiveReturnMode>): string => {
    const modeToUse = mode ? unref(mode) : activeReturnMode.value;

    if (modeToUse === ActiveReturnMode.ACTIVE_RETURN) {
      return translate({ path: 'GLOBAL.RETURN_TYPE_CONSTANTS', item: ActiveReturnMode.ACTIVE_RETURN });
    }

    if (modeToUse === ActiveReturnMode.EXCESS_RETURN) {
      return translate({ path: 'GLOBAL.RETURN_TYPE_CONSTANTS', item: ActiveReturnMode.EXCESS_RETURN });
    }

    return translate({
      path: 'GLOBAL.RETURN_TYPE_CONSTANTS',
      item: 'RETURN',
    });
  };

  const activeReturnModeText = computed(() => getActiveReturnModeText(activeReturnMode));

  return {
    activeReturnMode,
    activeReturnModeOptions,
    activeReturnModeText,
    getActiveReturnModeText,
    isActiveReturnStateActive,
    activeReturnRebalancingType,
  };
}

/**
 * This global state is used to track the current blmView id and
 * whether or not blmViw is active.
 */
const useBlmState = createGlobalState(
  (): {
    storeBlmViewId: Ref<string | undefined>;
    isBlmActive: Ref<boolean>;
  } => {
    const storeBlmViewId = ref<string | undefined>(undefined);
    const isBlmActive = ref(false);

    return {
      storeBlmViewId,
      isBlmActive,
    };
  },
);

/**
 * This composable is used to track the current blmView and its id,
 * the list of blmViews, and whether or not blmView is active.
 */
function useBlm(options: VQQueryOptions = {}): {
  storeBlmViewId: Ref<string | undefined>;
  isBlmActive: Ref<boolean>;
  blmViews: LoadingWhenEnabledQRT<BLMView[], unknown>;
  storeBlmView: Ref<BLMView | null>;
  doesPageHaveBlmImplemented: Ref<boolean>;
} {
  const state = useBlmState();
  const blmViews = useGetBlmViews(options);

  const route = useRoute();
  const { isOnPerformance, isOnEfficientFrontier } = useRouteChecks(route);

  const doesPageHaveBlmImplemented = computed(() => isOnPerformance.value || isOnEfficientFrontier.value);

  /**
   * if isBlmActive is false, return null. There is no storeBlmView if BLM View is not active.
   */
  const storeBlmView = computed(() => {
    if (!state.isBlmActive.value) return null;

    return blmViews.data.value?.find((view) => view.id === state.storeBlmViewId.value) ?? null;
  });

  return {
    ...state,
    blmViews,
    storeBlmView,
    doesPageHaveBlmImplemented,
  };
}

/**
 * This global state is used to track the current regime id and
 * whether or not regime analysis is active.
 */
const useCreateRegimeAnalysisState = createGlobalState(
  (): {
    storeRegimeId: Ref<string | undefined>;
    isRegimeAnalysisActive: Ref<boolean>;
    hoveredThresholdName: Ref<string | undefined>;
  } => {
    const storeRegimeId = ref<string | undefined>();
    const isRegimeAnalysisActive = ref(false);
    const hoveredThresholdName = ref<string | undefined>();

    return {
      storeRegimeId,
      isRegimeAnalysisActive,
      hoveredThresholdName,
    };
  },
);

/**
 * This composable is used to track the current regime and its id,
 * the list of regimes, and whether or not Regime Analysis mode is active.
 */
function useRegimeAnalysisState(options: VQQueryOptions = {}): {
  storeRegimeId: Ref<string | undefined>;
  storeRegime: Ref<RegimeAnalysisAttributeResponseDTO | null>;
  isRegimeAnalysisActive: Ref<boolean>;
  hoveredThresholdName: Ref<string | undefined>;
  regimeSets: UseQueryReturnType<RegimeAnalysisAttributeResponseDTO[], unknown>;
} {
  const state = useCreateRegimeAnalysisState();
  const regimeSets = useGetRegimeSets(options);

  /**
   * if isRegimeAnalysisActive is false, return null. There is no storeRegime if Regime Analysis mode is not active.
   */
  const storeRegime = computed(() => {
    if (!state.isRegimeAnalysisActive.value) return null;

    return regimeSets.data.value?.find((view) => view.attributeId === state.storeRegimeId.value) ?? null;
  });

  return {
    ...state,
    regimeSets,
    storeRegime,
  };
}

/**
 * We have some analysis modes that are not necessarily able to be combined with each other.
 * Place utilities regarding this in this composable
 *
 * if I activate a regime:
 * - if BLM is active, turn it off
 * - if i'm in Active Return or Excess Return, switch to Total Return
 *
 * if I activate BLM:
 * - if a regime is active, turn it off
 * - if i'm in Active Return or Excess Return, switch to Total Return
 *
 * if I activate Active Return or Excess Return:
 * - if BLM is active, turn it off
 * - if a regime is active, turn it off
 */
export default function useAnalysisModes(
  { shouldFetchAllViews }: { shouldFetchAllViews?: MaybeRef<boolean> } = { shouldFetchAllViews: false },
): {
  changeActiveReturnMode: (newMode: ActiveReturnMode) => void;
  activeReturnMode: Ref<ActiveReturnMode>;
  isActiveReturnStateActive: Ref<boolean>;
  activeReturnRebalancingType: Ref<ActiveReturnRebalancingType>;
  turnOffActiveReturn: () => void;
  resetAnalysisModes: () => void;

  getActiveReturnModeText: (mode?: MaybeRef<ActiveReturnMode>) => string;
  activeReturnModeOptions: Ref<{ value: ActiveReturnMode; text: string }[]>;
  activeReturnModeText: Ref<string>;

  storeBlmViewId: Ref<string | undefined>;
  storeBlmView: Ref<BLMView | null>;
  isBlmActive: Ref<boolean>;
  doesPageHaveBlmImplemented: Ref<boolean>;
  applyForwardLookingScenario: (view: BLMView) => void;
  turnOffBlm: () => void;

  storeRegimeId: Ref<string | undefined>;
  storeRegime: Ref<RegimeAnalysisAttributeResponseDTO | null>;
  hoveredThresholdName: Ref<string | undefined>;
  isRegimeAnalysisActive: Ref<boolean>;
  applyRegime: (regimeId: string) => void;
  setHoveredThresholdName: (newVal: string | undefined) => void;
  turnOffRegime: () => void;
} {
  const {
    activeReturnMode,
    getActiveReturnModeText,
    activeReturnModeText,
    activeReturnModeOptions,
    isActiveReturnStateActive,
    activeReturnRebalancingType,
  } = useActiveReturnMode();

  const { DEFAULT_ACTIVE_RETURN_REBALANCING_TYPE } = useDefaultActiveReturnRebalancingType();

  const { storeBlmViewId, isBlmActive, storeBlmView, doesPageHaveBlmImplemented } = useBlm({
    enabled: computed(() => unref(shouldFetchAllViews) ?? false),
  });
  const { storeRegimeId, isRegimeAnalysisActive, storeRegime, hoveredThresholdName } = useRegimeAnalysisState({
    enabled: computed(() => unref(shouldFetchAllViews) ?? false),
  });

  const turnOffBlm = (): void => {
    isBlmActive.value = false;
    storeBlmViewId.value = undefined;
  };
  const turnOffRegime = (): void => {
    isRegimeAnalysisActive.value = false;
    storeRegimeId.value = undefined;
    hoveredThresholdName.value = undefined;
  };
  const turnOffActiveReturn = (): void => {
    activeReturnMode.value = ActiveReturnMode.TOTAL_RETURN;
    activeReturnRebalancingType.value = DEFAULT_ACTIVE_RETURN_REBALANCING_TYPE;
  };

  const resetAnalysisModes = (): void => {
    turnOffActiveReturn();
    turnOffRegime();
    turnOffBlm();
  };

  const changeActiveReturnMode = (newMode: ActiveReturnMode): void => {
    turnOffBlm();
    turnOffRegime();

    activeReturnMode.value = newMode;
  };

  const applyForwardLookingScenario = (view: BLMView): void => {
    turnOffRegime();
    turnOffActiveReturn();

    // because in changing the active return mode above we automatically turn off BLM,
    // we wait one tick here to re-apply BLM
    nextTick(() => {
      storeBlmViewId.value = view.id;
      isBlmActive.value = true;
    });
  };

  const applyRegime = (regimeId: string): void => {
    turnOffBlm();
    turnOffActiveReturn();

    // because in changing the active return mode above we automatically turn off regimes,
    // we wait one tick here to re-apply the regime
    nextTick(() => {
      storeRegimeId.value = regimeId;
      isRegimeAnalysisActive.value = true;
    });
  };
  const setHoveredThresholdName = (newVal: string | undefined): void => {
    hoveredThresholdName.value = newVal;
  };

  return {
    changeActiveReturnMode,
    activeReturnMode: computed(() => activeReturnMode.value),
    isActiveReturnStateActive,
    activeReturnRebalancingType,
    turnOffActiveReturn,
    resetAnalysisModes,

    getActiveReturnModeText,
    activeReturnModeOptions,
    activeReturnModeText,

    storeBlmViewId: computed(() => storeBlmViewId.value),
    storeBlmView: computed(() => storeBlmView.value),
    isBlmActive: computed(() => isBlmActive.value),
    doesPageHaveBlmImplemented,
    applyForwardLookingScenario,
    turnOffBlm,

    storeRegimeId: computed(() => storeRegimeId.value),
    storeRegime: computed(() => storeRegime.value),
    hoveredThresholdName: computed(() => hoveredThresholdName.value),
    isRegimeAnalysisActive: computed(() => isRegimeAnalysisActive.value),
    applyRegime,
    setHoveredThresholdName,
    turnOffRegime,
  };
}
